import { useState, useEffect } from 'react';
import type { Event, EventsResponse } from '@/types/event';
import { getManualEvents, toEvent } from '@/services/manualEventService';

const API_URL = '/api/events';

interface UseEventsResult {
  events: Event[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch events from the API
 * Includes automatic retry logic and error handling
 */
export function useEvents(): UseEventsResult {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);

    try {
      // Add cache-busting timestamp to force fresh data
      const cacheBuster = `?_=${Date.now()}`;
      const response = await fetch(API_URL + cacheBuster);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data: EventsResponse = await response.json();

      // Manual events are best-effort: if Supabase is unreachable, still show Eventbrite events
      const { data: manualRows } = await getManualEvents();
      const manualEvents = manualRows.map(toEvent);

      const merged = [...(data.events || []), ...manualEvents].sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );

      setEvents(merged);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load events';
      setError(message);
      console.error('[REFUGE] Error loading events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  return { events, loading, error, refetch: fetchEvents };
}
